import { intlLocale, type Lang } from './langs';

export type Dict = Readonly<Record<string, string>>;
export type Params = Readonly<Record<string, string | number | null | undefined>>;
/** Dicionários de um conjunto de chaves nos três idiomas. */
export type Dicts<K extends string = string> = Readonly<Record<Lang, Readonly<Record<K, string>>>>;

const MARK = /\{(\w+)\}/g;

/** Troca `{nome}` pelos parâmetros; números saem no formato do idioma. */
export function interpolate(template: string, params: Params | undefined, lang: Lang): string {
  if (!params) return template;
  return template.replace(MARK, (all, name: string) => {
    const v = params[name];
    if (v == null) return all;
    return typeof v === 'number' ? new Intl.NumberFormat(intlLocale(lang)).format(v) : String(v);
  });
}

/** Marcadores `{x}` de um texto, para o teste de completude. */
export function placeholders(template: string): string[] {
  return [...template.matchAll(MARK)].map((m) => m[1] ?? '').sort();
}

/**
 * Traduz uma chave. Plural: com `count` numérico, procura `chave.one`, `chave.other`…
 * (categorias do Intl.PluralRules), caindo para `chave.other` e depois `chave`.
 * Chave ausente devolve a própria chave (o teste de completude impede que isso chegue ao usuário).
 */
export function translate(dicts: Dicts, lang: Lang, key: string, params?: Params): string {
  const d = dicts[lang] ?? dicts.en;
  const count = params?.count;
  let tpl: string | undefined;
  if (typeof count === 'number') {
    const cat = new Intl.PluralRules(intlLocale(lang)).select(count);
    tpl = d[`${key}.${cat}`] ?? d[`${key}.other`];
  }
  tpl ??= d[key] ?? dicts.en[key];
  return tpl == null ? key : interpolate(tpl, params, lang);
}

/** Junta dicionários (ex.: comuns + da aplicação), idioma a idioma. */
export function mergeDicts<A extends string, B extends string>(a: Dicts<A>, b: Dicts<B>): Dicts<A | B> {
  return {
    en: { ...a.en, ...b.en },
    'pt-BR': { ...a['pt-BR'], ...b['pt-BR'] },
    es: { ...a.es, ...b.es },
  } as Dicts<A | B>;
}

/** Problemas de completude entre idiomas (chave faltando, vazia, sobrando ou com marcadores diferentes). */
export function completenessIssues(dicts: Dicts): string[] {
  const issues: string[] = [];
  const base = dicts.en;
  const langs = Object.keys(dicts) as Lang[];
  for (const lang of langs) {
    const d = dicts[lang];
    for (const k of Object.keys(base)) {
      const v = d[k];
      if (v == null) issues.push(`${lang}: falta a chave "${k}"`);
      else if (!v.trim()) issues.push(`${lang}: chave "${k}" vazia`);
      else if (placeholders(v).join(',') !== placeholders(base[k] ?? '').join(',')) {
        issues.push(`${lang}: marcadores diferentes em "${k}" (${placeholders(v).join(',')} ≠ ${placeholders(base[k] ?? '').join(',')})`);
      }
    }
    for (const k of Object.keys(d)) {
      if (!(k in base)) issues.push(`${lang}: chave "${k}" não existe em inglês`);
    }
  }
  return issues;
}

/** Nomes próprios que levam letras de outro idioma de propósito (cidade de exemplo, nome do idioma). */
const PROPER_NAMES = /São Paulo|Español|Português/g;

/**
 * Texto no idioma errado: letras que só existem em português (ç, ã, õ) no espanhol ou no inglês,
 * letras e sinais que só existem em espanhol (ñ, ¿, ¡) no português ou no inglês, e o nome antigo
 * do produto ("ONE"; "Workspace ONE" é de terceiros e fica).
 */
export function languageIssues(dicts: Dicts): string[] {
  const issues: string[] = [];
  const rules: [Lang, RegExp][] = [
    ['en', /[çãõñ¿¡]|\b(não|você|são|configurações)\b/i],
    ['pt-BR', /[ñ¿¡]/],
    ['es', /[çãõ]|\b(não|você|nenhum|também|então|recolector(es)?)\b/i],
  ];
  for (const [lang, bad] of rules) {
    for (const [k, v] of Object.entries(dicts[lang] ?? {})) {
      const text = v.replace(PROPER_NAMES, '');
      if (bad.test(text)) issues.push(`${lang}: "${k}" parece estar em outro idioma: ${v.slice(0, 80)}`);
      if (/\bONE\b/.test(text.replace(/Workspace ONE/g, ''))) issues.push(`${lang}: "${k}" usa o nome antigo ONE`);
    }
  }
  return issues;
}

export interface Formatters {
  date(v: string | number | Date | null | undefined): string;
  dateTime(v: string | number | Date | null | undefined): string;
  number(v: number | null | undefined): string;
  relative(v: string | number | Date | null | undefined, now?: number): string;
}

function toDate(v: string | number | Date): Date {
  if (v instanceof Date) return v;
  // data sem hora ("2029-10-09") é um dia do calendário, não meia-noite UTC: senão vira o dia anterior no Brasil
  const d = typeof v === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(v) : null;
  return d ? new Date(Number(d[1]), Number(d[2]) - 1, Number(d[3])) : new Date(v);
}

/** Datas e números no formato local do idioma (HU-012: mudam na hora com o idioma). */
export function formatters(lang: Lang): Formatters {
  const loc = intlLocale(lang);
  const df = new Intl.DateTimeFormat(loc, { dateStyle: 'medium' });
  const dtf = new Intl.DateTimeFormat(loc, { dateStyle: 'short', timeStyle: 'medium' });
  const nf = new Intl.NumberFormat(loc);
  const rtf = new Intl.RelativeTimeFormat(loc, { numeric: 'auto' });
  return {
    date: (v) => (v == null || v === '' ? '—' : df.format(toDate(v))),
    dateTime: (v) => (v == null || v === '' ? '—' : dtf.format(toDate(v))),
    number: (v) => (v == null ? '—' : nf.format(v)),
    relative: (v, now = Date.now()) => {
      if (v == null || v === '') return '—';
      const diff = (toDate(v).getTime() - now) / 1000;
      const abs = Math.abs(diff);
      if (abs < 45) return rtf.format(Math.round(diff), 'second');
      if (abs < 2700) return rtf.format(Math.round(diff / 60), 'minute');
      if (abs < 79200) return rtf.format(Math.round(diff / 3600), 'hour');
      if (abs < 2592000) return rtf.format(Math.round(diff / 86400), 'day');
      if (abs < 31536000) return rtf.format(Math.round(diff / 2592000), 'month');
      return rtf.format(Math.round(diff / 31536000), 'year');
    },
  };
}
